import React from 'react';
import {
    FaChevronRight,
    FaChevronDown,
    FaCircle,
    FaChartBar,
    FaUser,
    FaCompress,
    FaMobileAlt,
    FaCog,
    FaInfoCircle,
    FaLightbulb
} from 'react-icons/fa';
import { REPORT_TYPES, SECTIONS } from '../constants';

const icons = {
    advanced: <FaChartBar />,
    dynamic: <FaUser />,
    compact: <FaCompress />,
    vizapp: <FaMobileAlt />,
    others: <FaCog />
};

const sectionIcons = {
    about: <FaInfoCircle />,
    tips: <FaLightbulb />
};

const NavigationTree = ({
    profileContent,
    activeProfile,
    activeReportType,
    activeSection,
    onChangeReportType,
    onChangeSection,
    expandedTypes,
    onToggleExpand
}) => {
    const profileData = profileContent[activeProfile] || {};

    // Check if a section has content
    const hasContent = (reportType, section) => {
        const rtData = profileData[reportType];
        if (!rtData) return false;

        if (section === 'about') {
            const about = rtData.about;
            return about && (
                Object.values(about.header || {}).some(v => v) ||
                Object.values(about.text || {}).some(v => v)
            );
        }

        if (section === 'tips') {
            const tips = rtData.tips;
            return tips && tips.length > 0;
        }

        return false;
    };

    // Filter report types that exist in this profile
    const availableReportTypes = REPORT_TYPES.filter(rt =>
        profileData[rt.id] !== undefined
    );

    return (
        <nav className="pfc-nav-tree">
            <div className="pfc-nav-header">
                <span className="pfc-nav-label">Reports & Sections</span>
            </div>

            <ul className="pfc-nav-list">
                {availableReportTypes.map(reportType => {
                    const isExpanded = expandedTypes[reportType.id];
                    const isActiveRT = activeReportType === reportType.id;
                    const Icon = icons[reportType.id] || <FaCog />;

                    return (
                        <li key={reportType.id} className="pfc-nav-item">
                            {/* Report Type Node */}
                            <button
                                className={`pfc-nav-node ${isActiveRT ? 'active' : ''}`}
                                onClick={() => onToggleExpand(reportType.id)}
                            >
                                <span className={`pfc-nav-chevron ${isExpanded ? 'expanded' : ''}`}>
                                    <FaChevronRight />
                                </span>
                                <span className="pfc-nav-icon-main">{Icon}</span>
                                <span className="pfc-nav-text">{reportType.label}</span>
                            </button>

                            {/* Sections (when expanded) */}
                            {isExpanded && (
                                <ul className="pfc-nav-sublist">
                                    {/* About Section */}
                                    {profileData[reportType.id]?.about && (
                                        <li>
                                            <button
                                                className={`pfc-nav-subnode ${isActiveRT && activeSection === 'about' ? 'active' : ''
                                                    }`}
                                                onClick={() => {
                                                    onChangeReportType(reportType.id);
                                                    onChangeSection('about');
                                                }}
                                            >
                                                <span className="pfc-nav-subicon">{sectionIcons.about}</span>
                                                <span>{SECTIONS.about.label}</span>
                                                <span className="pfc-nav-status-dot">
                                                    {hasContent(reportType.id, 'about') && <FaCircle />}
                                                </span>
                                            </button>
                                        </li>
                                    )}

                                    {/* Tips Section */}
                                    {profileData[reportType.id]?.tips !== undefined && (
                                        <li>
                                            <button
                                                className={`pfc-nav-subnode ${isActiveRT && activeSection === 'tips' ? 'active' : ''
                                                    }`}
                                                onClick={() => {
                                                    onChangeReportType(reportType.id);
                                                    onChangeSection('tips');
                                                }}
                                            >
                                                <span className="pfc-nav-subicon">{sectionIcons.tips}</span>
                                                <span>{SECTIONS.tips.label}</span>
                                                {profileData[reportType.id]?.tips?.length > 0 && (
                                                    <span className="pfc-tips-count-badge">
                                                        {profileData[reportType.id].tips.length}
                                                    </span>
                                                )}
                                                <span className="pfc-nav-status-dot">
                                                    {hasContent(reportType.id, 'tips') && <FaCircle />}
                                                </span>
                                            </button>
                                        </li>
                                    )}

                                    {/* Others Section (for 'others' report type) */}
                                    {reportType.id === 'others' && (
                                        <li>
                                            <button
                                                className={`pfc-nav-subnode ${isActiveRT && activeSection === 'others' ? 'active' : ''
                                                    }`}
                                                onClick={() => {
                                                    onChangeReportType(reportType.id);
                                                    onChangeSection('others');
                                                }}
                                            >
                                                <span className="pfc-nav-subicon">{icons.others}</span>
                                                <span>Other Fields</span>
                                            </button>
                                        </li>
                                    )}
                                </ul>
                            )}
                        </li>
                    );
                })}
            </ul>
        </nav>
    );
};

export default NavigationTree;
